import {
  Button,
  DatePicker,
  Dialog,
  DialogPanel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TextInput,
  Textarea,
} from "@tremor/react";
import { Clock, Pause, Plus, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
// Mutations called directly; the page invalidates the maintenance cache via
// the query client after each successful save.
import {
  createMaintenanceWindow,
  deleteMaintenanceWindow,
  updateMaintenanceWindow,
} from "../api/maintenance.js";
import { useMaintenanceWindows } from "../queries/maintenance.js";
import { useMonitors } from "../queries/monitors.js";
import { useQueryClient } from "@tanstack/react-query";
import { keys } from "../queryKeys.js";
import Badge from "../components/Badge.jsx";

import { useTranslation } from "react-i18next";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Combine a Date object + "HH:MM" string into an ISO timestamp.
 * Returns null if either is missing.
 */
function combineDatetime(date, timeStr) {
  if (!date || !timeStr) return null;
  const [h, m] = timeStr.split(":").map(Number);
  const d = new Date(date);
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}

/**
 * Split an ISO timestamp into a Date object and "HH:MM" string (local time).
 */
function parseDatetime(iso) {
  if (!iso) return { date: null, time: "00:00" };
  const d = new Date(iso);
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return { date: d, time: `${hours}:${minutes}` };
}

function formatDateRange(starts_at, ends_at, i18n) {
  const fmt = (iso) =>
    new Date(iso).toLocaleString(i18n.language, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  return `${fmt(starts_at)} → ${fmt(ends_at)}`;
}

const STATE_BADGE_COLOR = { active: "orange", upcoming: "teal", past: "gray" };

const EMPTY_FORM = {
  name: "",
  comment: "",
  starts_date: null,
  starts_time: "00:00",
  ends_date: null,
  ends_time: "01:00",
  monitor_ids: [],
};

// ---------------------------------------------------------------------------
// DateTimePicker — Tremor calendar + time input
// ---------------------------------------------------------------------------

function DateTimePicker({ label, required, date, time, onDateChange, onTimeChange }) {
  const { t } = useTranslation("maintenance");
  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <DatePicker
        value={date}
        onValueChange={onDateChange}
        placeholder={t("drawer.pick_date")}
        className="w-full"
      />
      <input
        type="time"
        value={time}
        onChange={(e) => onTimeChange(e.target.value)}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create / Edit drawer
// ---------------------------------------------------------------------------

function WindowDrawer({ window: editWindow, monitors, onClose, onSaved }) {
  const { t } = useTranslation("maintenance");
  const isEdit = !!editWindow;
  const [form, setForm] = useState(() => {
    if (isEdit) {
      const start = parseDatetime(editWindow.starts_at);
      const end = parseDatetime(editWindow.ends_at);
      return {
        name: editWindow.name,
        comment: editWindow.comment ?? "",
        starts_date: start.date,
        starts_time: start.time,
        ends_date: end.date,
        ends_time: end.time,
        monitor_ids: editWindow.monitor_ids ?? [],
      };
    }
    return { ...EMPTY_FORM };
  });
  const [saving, setSaving] = useState(false);

  function set(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function toggleMonitor(id) {
    setForm((prev) => {
      const already = prev.monitor_ids.includes(id);
      return {
        ...prev,
        monitor_ids: already
          ? prev.monitor_ids.filter((m) => m !== id)
          : [...prev.monitor_ids, id],
      };
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error(t("errors.name_required"));
      return;
    }
    const startsAt = combineDatetime(form.starts_date, form.starts_time);
    const endsAt = combineDatetime(form.ends_date, form.ends_time);
    if (!startsAt || !endsAt) {
      toast.error(t("errors.dates_required"));
      return;
    }
    if (new Date(endsAt) <= new Date(startsAt)) {
      toast.error(t("errors.time_logic"));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        comment: form.comment.trim() || null,
        starts_at: startsAt,
        ends_at: endsAt,
        monitor_ids: form.monitor_ids,
      };

      if (isEdit) {
        await updateMaintenanceWindow(editWindow.id, payload);
        toast.success(t("drawer.success_edit"));
      } else {
        await createMaintenanceWindow(payload);
        toast.success(t("drawer.success_new"));
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.error ?? t("errors.save_error"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} static>
      {/* DialogPanel positioned as a right-side drawer */}
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-w-md flex-col rounded-none bg-white p-0 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 className="text-base font-semibold text-gray-900">
            {isEdit ? t("drawer.title_edit") : t("drawer.title_new")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form — no overflow-y-auto here so DatePicker calendar isn't clipped */}
        <form onSubmit={handleSubmit} className="flex flex-1 flex-col">
          <div className="flex-1 space-y-5 px-5 py-5">
            {/* Name */}
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                {t("drawer.name")} <span className="text-red-500">*</span>
              </label>
              <TextInput
                value={form.name}
                onValueChange={(v) => set("name", v)}
                placeholder={t("drawer.name_placeholder")}
              />
            </div>

            {/* Comment */}
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                {t("drawer.comment")}{" "}
                <span className="font-normal text-gray-400">
                  {t("drawer.comment_help")}
                </span>
              </label>
              <Textarea
                rows={3}
                value={form.comment}
                onValueChange={(v) => set("comment", v)}
                placeholder={t("drawer.comment_placeholder")}
              />
            </div>

            {/* Date + time pickers */}
            <div className="grid grid-cols-2 gap-4">
              <DateTimePicker
                label={t("drawer.start")}
                required
                date={form.starts_date}
                time={form.starts_time}
                onDateChange={(d) => set("starts_date", d)}
                onTimeChange={(t) => set("starts_time", t)}
              />
              <DateTimePicker
                label={t("drawer.end")}
                required
                date={form.ends_date}
                time={form.ends_time}
                onDateChange={(d) => set("ends_date", d)}
                onTimeChange={(t) => set("ends_time", t)}
              />
            </div>

            {/* Monitor selection */}
            <div>
              <label className="mb-2 block text-xs font-medium text-gray-700">
                {t("drawer.monitors_title")}{" "}
                <span className="font-normal text-gray-400">
                  {t("drawer.monitors_selected", { count: form.monitor_ids.length })}
                </span>
              </label>
              {monitors.length === 0 ? (
                <p className="text-xs text-gray-400">{t("drawer.no_monitors")}</p>
              ) : (
                <div className="max-h-52 overflow-y-auto rounded-md border border-gray-200">
                  {monitors.map((m) => (
                    <label
                      key={m.id}
                      className="flex cursor-pointer items-center gap-3 border-b border-gray-100 px-3 py-2 last:border-b-0 hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={form.monitor_ids.includes(m.id)}
                        onChange={() => toggleMonitor(m.id)}
                        className="h-4 w-4 rounded border-gray-300 text-teal-600"
                      />
                      <span className="flex-1 text-sm text-gray-800">
                        {m.name}
                      </span>
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">
                        {m.type}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              <p className="mt-1.5 text-xs text-gray-400">
                {t("drawer.help_footer")}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t border-gray-200 px-5 py-4">
            <Button variant="secondary" type="button" onClick={onClose}>
              {t("common:common.cancel")}
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? t("drawer.saving") : isEdit ? t("drawer.save") : t("drawer.create")}
            </Button>
          </div>
        </form>
      </DialogPanel>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Window row
// ---------------------------------------------------------------------------

function WindowRow({ window, onEdit, onDelete }) {
  const { t, i18n } = useTranslation("maintenance");
  const monitorCount = window.monitor_ids?.length ?? 0;

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-gray-900">{window.name}</p>
          <Badge color={STATE_BADGE_COLOR[window.state] ?? "gray"}>
            {t(`states.${window.state}`)}
          </Badge>
        </div>
        {window.comment && (
          <p className="mt-0.5 text-xs text-gray-500 line-clamp-1">
            {window.comment}
          </p>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap text-gray-500">
        <div className="flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5 shrink-0" />
          {formatDateRange(window.starts_at, window.ends_at, i18n)}
        </div>
      </TableCell>
      <TableCell className="text-gray-500">
        {monitorCount === 0 ? (
          <span className="text-gray-400">{t("table.all_monitors")}</span>
        ) : (
          t("table.monitors_count", { count: monitorCount })
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="xs" onClick={() => onEdit(window)}>
            {t("common:common.edit")}
          </Button>
          <Button
            variant="secondary"
            size="xs"
            color="red"
            icon={Trash2}
            onClick={() => onDelete(window)}
          />
        </div>
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function Maintenance() {
  const { t } = useTranslation(["maintenance", "common"]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingWindow, setEditingWindow] = useState(null);

  const qc = useQueryClient();
  const windowsQuery = useMaintenanceWindows();
  const monitorsQuery = useMonitors();
  const windows = windowsQuery.data ?? [];
  const monitors = monitorsQuery.data?.data ?? [];
  const loading = windowsQuery.isPending || monitorsQuery.isPending;

  useEffect(() => {
    if (windowsQuery.isError) toast.error(t("errors.load_error"));
  }, [windowsQuery.isError, t]);

  const reload = () => qc.invalidateQueries({ queryKey: keys.maintenance.all });

  function openCreate() {
    setEditingWindow(null);
    setDrawerOpen(true);
  }

  function openEdit(window) {
    setEditingWindow(window);
    setDrawerOpen(true);
  }

  function closeDrawer() {
    setDrawerOpen(false);
    setEditingWindow(null);
  }

  function handleSaved() {
    closeDrawer();
    reload();
  }

  async function handleDelete(window) {
    if (!confirm(t("table.delete_confirm", { name: window.name }))) return;
    try {
      await deleteMaintenanceWindow(window.id);
      toast.success(t("table.success_delete"));
      reload();
    } catch {
      toast.error(t("errors.save_error"));
    }
  }

  const activeWindows = windows.filter((w) => w.state === "active");
  const upcomingWindows = windows.filter((w) => w.state === "upcoming");
  const pastWindows = windows.filter((w) => w.state === "past");

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t("subtitle")}
          </p>
        </div>
        <Button icon={Plus} onClick={openCreate}>
          {t("new_window")}
        </Button>
      </div>

      {/* Active banner */}
      {activeWindows.length > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3">
          <Pause className="h-5 w-5 shrink-0 text-orange-500" />
          <p className="text-sm text-orange-800">
            <span className="font-semibold">
              {t("banner.active_count", { count: activeWindows.length })}:
            </span>{" "}
            {activeWindows.map((w) => w.name).join(", ")} — {t("banner.suppressed_info")}
          </p>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <p className="text-sm text-gray-400">{t("loading")}</p>
      ) : windows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white py-16 text-center">
          <Pause className="mx-auto mb-3 h-8 w-8 text-gray-300" />
          <p className="text-sm font-medium text-gray-500">
            {t("empty.title")}
          </p>
          <p className="mt-1 text-xs text-gray-400">
            {t("empty.description")}
          </p>
          <Button className="mt-4" onClick={openCreate}>
            {t("empty.button")}
          </Button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          {[
            { label: t("states.active"), items: activeWindows },
            { label: t("states.upcoming"), items: upcomingWindows },
            { label: t("states.past"), items: pastWindows },
          ]
            .filter(({ items }) => items.length > 0)
            .map(({ label, items }) => (
              <div key={label}>
                <div className="border-b border-gray-100 bg-gray-50 px-4 py-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {label}
                  </p>
                </div>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>{t("table.name")}</TableHeaderCell>
                      <TableHeaderCell>{t("table.schedule")}</TableHeaderCell>
                      <TableHeaderCell>{t("table.monitors")}</TableHeaderCell>
                      <TableHeaderCell></TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((w) => (
                      <WindowRow
                        key={w.id}
                        window={w}
                        onEdit={openEdit}
                        onDelete={handleDelete}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            ))}
        </div>
      )}

      {/* Drawer */}
      {drawerOpen && (
        <WindowDrawer
          window={editingWindow}
          monitors={monitors}
          onClose={closeDrawer}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
